import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { subscribeToPush, unsubscribeFromPush, currentSubscription, isPushSupported } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Sun, Moon, Trophy, Award, Bell, Bug, CreditCard, Smartphone, Upload, Trash2 } from "lucide-react";
import AvatarTiered from "@/components/AvatarTiered";

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();

  const [profile, setProfile] = useState({
    display_name: user?.display_name || "",
    credentials: user?.credentials || "",
  });
  const [privacy, setPrivacy] = useState({
    privacy_public: user?.privacy_public !== false,
    show_email: !!user?.show_email,
  });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const defaultPrefs = { dm: true, friend_request: true, vote: true, verified: true, reply: true, update: true, response: true };
  const [prefs, setPrefs] = useState({ ...defaultPrefs, ...(user?.notify_prefs || {}) });
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const supported = await isPushSupported();
      setPushSupported(supported);
      if (supported) setPushEnabled(!!(await currentSubscription()));
    })();
  }, []);

  const togglePush = async (v) => {
    try {
      if (v) { await subscribeToPush(); setPushEnabled(true); toast.success("Browser push enabled"); }
      else { await unsubscribeFromPush(); setPushEnabled(false); toast.success("Browser push disabled"); }
    } catch (err) { toast.error(err.message || "Push setup failed"); }
  };

  const [bug, setBug] = useState({ title: "", description: "", url: "" });
  const [payout, setPayout] = useState(null);

  useEffect(() => { api.get("/payouts/status").then((r) => setPayout(r.data)).catch(() => setPayout({ enabled: false })); }, []);

  const submitBug = async () => {
    if (bug.title.length < 3 || bug.description.length < 5) return toast.error("Add a title and description");
    try {
      await api.post("/bugs", { ...bug, url: bug.url || window.location.href });
      toast.success("Bug report sent — thanks");
      setBug({ title: "", description: "", url: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const connectPayouts = async () => {
    try {
      const { data } = await api.post("/payouts/onboard", { origin_url: window.location.origin });
      if (data.onboarding_url) window.location.href = data.onboarding_url;
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  if (!user) return <Navigate to="/login" />;

  const saveProfile = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me", profile);
      setUser(data);
      localStorage.setItem("eco_user", JSON.stringify(data));
      toast.success("Profile updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const savePrivacy = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/users/me", privacy);
      setUser(data);
      localStorage.setItem("eco_user", JSON.stringify(data));
      toast.success("Privacy updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("PNG, JPG or WebP only");
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5 MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser(data);
      localStorage.setItem("eco_user", JSON.stringify(data));
      toast.success("Avatar updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const clearAvatar = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/users/me/avatar");
      setUser(data);
      localStorage.setItem("eco_user", JSON.stringify(data));
      toast.success("Avatar removed");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const changePw = async () => {
    if (pw.new_password.length < 6) return toast.error("New password must be 6+ chars");
    setBusy(true);
    try {
      await api.post("/users/password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password changed");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const savePrefs = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/users/notify-prefs", prefs);
      setUser(data);
      localStorage.setItem("eco_user", JSON.stringify(data));
      toast.success("Notification preferences saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const disableAll = () => setPrefs(Object.fromEntries(Object.keys(prefs).map((k) => [k, false])));

  return (
    <div className="mx-auto max-w-4xl px-6 py-16">
      <div className="mono text-xs text-primary">// account · settings</div>
      <h1 className="mt-2 text-5xl font-black tracking-tighter">Your account.</h1>

      <div className="mt-8 flex flex-wrap items-center gap-6 border border-border p-6">
        <AvatarTiered user={user} size="lg" testId="settings-avatar" />
        <div className="flex-1">
          <div className="text-2xl font-bold tracking-tight">{user.display_name}</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{user.role} {user.qualified && "· verified"}</div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="mono text-2xl text-primary flex items-center gap-1"><Trophy className="h-4 w-4" />{user.points ?? 0}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">points</div>
          </div>
          <Button variant="outline" data-testid="settings-profile-link" className="rounded-none" onClick={() => nav(`/u/${user.id}`)}>View public profile</Button>
        </div>
      </div>

      <Tabs defaultValue="profile" className="mt-10">
        <TabsList className="rounded-none border border-border bg-transparent p-0">
          <TabsTrigger value="profile" data-testid="tab-profile" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Profile</TabsTrigger>
          <TabsTrigger value="privacy" data-testid="tab-privacy" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Privacy</TabsTrigger>
          <TabsTrigger value="appearance" data-testid="tab-appearance" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Appearance</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Notifications</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Payouts</TabsTrigger>
          <TabsTrigger value="bug" data-testid="tab-bug" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Report a bug</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6 border border-border p-6 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Avatar</Label>
            <div className="mt-2 flex items-center gap-4">
              <AvatarTiered user={user} size="lg" />
              <div className="flex flex-col gap-2">
                <label className="inline-flex cursor-pointer items-center gap-2 border border-primary bg-primary/10 px-3 py-2 text-xs uppercase tracking-widest text-primary hover:bg-primary/20">
                  <Upload className="h-3 w-3" />Upload image
                  <input
                    data-testid="avatar-upload-input"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => uploadAvatar(e.target.files?.[0])}
                  />
                </label>
                {user.avatar_url && (
                  <button
                    data-testid="avatar-clear"
                    type="button"
                    onClick={clearAvatar}
                    disabled={busy}
                    className="inline-flex items-center gap-1 border border-border px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-destructive hover:border-destructive"
                  >
                    <Trash2 className="h-3 w-3" />Remove
                  </button>
                )}
                <div className="text-[10px] text-muted-foreground">PNG · JPG · WebP · max 5 MB</div>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Display name</Label>
            <Input data-testid="set-name" value={profile.display_name} onChange={(e) => setProfile({ ...profile, display_name: e.target.value })} className="mt-2 rounded-none bg-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Credentials / bio</Label>
            <Textarea data-testid="set-credentials" rows={4} value={profile.credentials} onChange={(e) => setProfile({ ...profile, credentials: e.target.value })} className="mt-2 rounded-none bg-input" placeholder="Institution, degree, field expertise…" />
          </div>
          <Button onClick={saveProfile} disabled={busy} data-testid="save-profile" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">Save profile</Button>
        </TabsContent>

        <TabsContent value="privacy" className="mt-6 border border-border p-6 space-y-5">
          <Row label="Public profile" desc="Show your name and points on the leaderboard and profile listings." testid="privacy-public">
            <Switch data-testid="privacy-public" checked={privacy.privacy_public} onCheckedChange={(v) => setPrivacy({ ...privacy, privacy_public: v })} />
          </Row>
          <Row label="Show email publicly" desc="Display your email address on your profile page. Off by default." testid="privacy-email">
            <Switch data-testid="privacy-email" checked={privacy.show_email} onCheckedChange={(v) => setPrivacy({ ...privacy, show_email: v })} />
          </Row>
          <Button onClick={savePrivacy} disabled={busy} data-testid="save-privacy" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">Save privacy</Button>
        </TabsContent>

        <TabsContent value="appearance" className="mt-6 border border-border p-6 space-y-5">
          <Row label="Theme" desc={`Currently: ${theme}. Toggle between the dark Volt Green theme and a light Sage theme.`} testid="theme-row">
            <div className="flex items-center gap-3">
              <Sun className={`h-4 w-4 ${theme === "light" ? "text-primary" : "text-muted-foreground"}`} />
              <Switch data-testid="theme-toggle" checked={theme === "dark"} onCheckedChange={toggle} />
              <Moon className={`h-4 w-4 ${theme === "dark" ? "text-primary" : "text-muted-foreground"}`} />
            </div>
          </Row>
        </TabsContent>

        <TabsContent value="notifications" className="mt-6 border border-border p-6 space-y-5">
          <div className="flex items-center justify-between border-b border-border pb-4">
            <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest"><Bell className="h-3 w-3" />Ping categories</div>
            <button data-testid="disable-all-notifs" onClick={disableAll} className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary">Disable all</button>
          </div>
          <div className="border border-primary/40 bg-primary/5 p-4 flex items-start justify-between gap-4">
            <div>
              <div className="font-bold tracking-tight">Browser push</div>
              <div className="mt-1 text-sm text-muted-foreground">{pushSupported ? "Get real desktop / mobile push notifications even when Eco Chamber is closed." : "This browser does not support Web Push."}</div>
              <div className="mt-2 text-xs text-muted-foreground flex items-start gap-2"><Smartphone className="h-3 w-3 mt-0.5 text-primary" />On iOS 16.4+ push works only after adding to Home Screen — tap Share → "Add to Home Screen" in Safari, open the icon, then enable Browser Push here.</div>
            </div>
            <div className="pt-1">
              <Switch data-testid="push-toggle" disabled={!pushSupported} checked={pushEnabled} onCheckedChange={togglePush} />
            </div>
          </div>
          <PrefRow k="dm" label="Direct messages" desc="When someone sends you a DM." prefs={prefs} setPrefs={setPrefs} />
          <PrefRow k="friend_request" label="Friend requests" desc="Incoming friend requests." prefs={prefs} setPrefs={setPrefs} />
          <PrefRow k="vote" label="Votes on my proposals" desc="Every time someone upvotes an idea you posted." prefs={prefs} setPrefs={setPrefs} />
          <PrefRow k="verified" label="Verified Success + rewards" desc="Proposal filtered, verified as implemented, or response accepted." prefs={prefs} setPrefs={setPrefs} />
          <PrefRow k="reply" label="Replies to my threads" desc="Someone replied in a chat room thread you started." prefs={prefs} setPrefs={setPrefs} />
          <PrefRow k="update" label="Friend updates" desc="A friend posted a project or enquiry." prefs={prefs} setPrefs={setPrefs} />
          <PrefRow k="response" label="Company responses" desc="Someone responded to your company request." prefs={prefs} setPrefs={setPrefs} />
          <Button onClick={savePrefs} disabled={busy} data-testid="save-notifs" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">Save preferences</Button>
        </TabsContent>

        <TabsContent value="payouts" className="mt-6 border border-border p-6 space-y-5">
          <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest"><CreditCard className="h-3 w-3" />Stripe Connect payouts</div>
          <p className="text-sm text-muted-foreground">Connect your Stripe account so accepted bounties are transferred to you automatically. Setup takes ~2 minutes via Stripe's hosted onboarding.</p>
          {payout ? (
            payout.connected ? (
              <div className="border border-border p-4">
                <div className="text-xs uppercase tracking-widest text-primary">Connected</div>
                <div className="mt-2 mono text-xs text-muted-foreground break-all">{payout.account_id}</div>
                <div className="mt-2 text-sm">Payouts enabled: <span className={payout.payouts_enabled ? "text-primary" : "text-destructive"}>{payout.payouts_enabled ? "yes" : "no — finish onboarding"}</span></div>
                {!payout.payouts_enabled && (
                  <Button data-testid="continue-onboarding" onClick={connectPayouts} className="mt-4 rounded-none bg-primary text-primary-foreground hover:bg-primary/90">Continue onboarding</Button>
                )}
              </div>
            ) : (
              <Button data-testid="connect-stripe" onClick={connectPayouts} disabled={!payout.enabled} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><CreditCard className="mr-2 h-4 w-4" />Connect Stripe payouts</Button>
            )
          ) : (
            <div className="text-sm text-muted-foreground">Checking payout status…</div>
          )}
        </TabsContent>

        <TabsContent value="bug" className="mt-6 border border-border p-6 space-y-4">
          <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest"><Bug className="h-3 w-3" />Report a bug</div>
          <p className="text-sm text-muted-foreground">Every report pings the platform admin in real time. Include steps to reproduce for the fastest fix.</p>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Title</Label><Input data-testid="bug-title" value={bug.title} onChange={(e) => setBug({ ...bug, title: e.target.value })} className="mt-2 rounded-none bg-input" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">Description</Label><Textarea data-testid="bug-desc" rows={5} value={bug.description} onChange={(e) => setBug({ ...bug, description: e.target.value })} className="mt-2 rounded-none bg-input" placeholder="What did you expect? What happened? Steps to reproduce…" /></div>
          <div><Label className="text-xs uppercase tracking-widest text-muted-foreground">URL where it happened (optional)</Label><Input data-testid="bug-url" value={bug.url} onChange={(e) => setBug({ ...bug, url: e.target.value })} className="mt-2 rounded-none bg-input mono" placeholder={typeof window !== "undefined" ? window.location.href : ""} /></div>
          <Button onClick={submitBug} data-testid="bug-submit" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><Bug className="mr-2 h-4 w-4" />Send report</Button>
        </TabsContent>


        <TabsContent value="security" className="mt-6 border border-border p-6 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Current password</Label>
            <Input data-testid="cur-pw" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-2 rounded-none bg-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">New password</Label>
            <Input data-testid="new-pw" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-2 rounded-none bg-input" />
          </div>
          <div className="flex justify-between">
            <Button onClick={changePw} disabled={busy} data-testid="save-password" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90">Change password</Button>
            <Button variant="destructive" data-testid="settings-signout" className="rounded-none" onClick={() => { logout(); nav("/"); }}>Sign out</Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Row = ({ label, desc, children }) => (
  <div className="flex items-start justify-between gap-6">
    <div>
      <div className="font-bold tracking-tight">{label}</div>
      <div className="mt-1 text-sm text-muted-foreground">{desc}</div>
    </div>
    <div className="pt-1">{children}</div>
  </div>
);

const PrefRow = ({ k, label, desc, prefs, setPrefs }) => (
  <div className="flex items-start justify-between gap-6">
    <div>
      <div className="font-bold tracking-tight">{label}</div>
      <div className="mt-1 text-sm text-muted-foreground">{desc}</div>
    </div>
    <div className="pt-1">
      <Switch data-testid={`pref-${k}`} checked={!!prefs[k]} onCheckedChange={(v) => setPrefs({ ...prefs, [k]: v })} />
    </div>
  </div>
);
